"use client";

import { createBrowserStore } from "@/utils/browserStore";
import { isLocaleCode, type LocaleCode, type ThemeMode } from "@/shared/types/surface";
import { STEM_SUBJECT_IDS, type SubjectCategoryId } from "@/data/educationalLibraries";

export const THEME_STORAGE_KEY = "whiteboard_theme_v1";
export const CATEGORY_STORAGE_KEY = "whiteboard_subject_selection_v2";
export const LOCALE_STORAGE_KEY = "whiteboard_locale_v1";
export const STROKE_SMOOTHING_STORAGE_KEY = "whiteboard_stroke_smoothing_v1";
export const DRAWING_TOOL_PREFS_STORAGE_KEY = "whiteboard_drawing_tool_prefs_v1";

export type ExcalidrawFillStyle =
  | "hachure"
  | "cross-hatch"
  | "solid"
  | "zigzag";
export type ExcalidrawStrokeStyle = "solid" | "dashed" | "dotted";
export type ExcalidrawStrokeRoundness = "round" | "sharp";

export interface DrawingToolPreferences {
  readonly currentItemStrokeColor: string;
  readonly currentItemBackgroundColor: string;
  readonly currentItemFillStyle: ExcalidrawFillStyle;
  readonly currentItemStrokeWidth: number;
  readonly currentItemStrokeStyle: ExcalidrawStrokeStyle;
  readonly currentItemRoughness: number;
  readonly currentItemRoundness: ExcalidrawStrokeRoundness;
  readonly currentItemOpacity: number;
}

export const DEFAULT_DRAWING_TOOL_PREFS: DrawingToolPreferences = Object.freeze({
  currentItemStrokeColor: "#1e1e1e",
  currentItemBackgroundColor: "transparent",
  currentItemFillStyle: "solid",
  currentItemStrokeWidth: 1,
  currentItemStrokeStyle: "solid",
  currentItemRoughness: 0,
  currentItemRoundness: "round",
  currentItemOpacity: 100,
});

const VALID_FILL_STYLES: ReadonlySet<ExcalidrawFillStyle> = new Set([
  "hachure",
  "cross-hatch",
  "solid",
  "zigzag",
]);

const VALID_STROKE_STYLES: ReadonlySet<ExcalidrawStrokeStyle> = new Set([
  "solid",
  "dashed",
  "dotted",
]);

const VALID_ROUNDNESS_TYPES: ReadonlySet<ExcalidrawStrokeRoundness> = new Set([
  "round",
  "sharp",
]);

export function sanitizeDrawingToolPrefs(raw: unknown): DrawingToolPreferences {
  if (!raw || typeof raw !== "object") {
    return DEFAULT_DRAWING_TOOL_PREFS;
  }
  const obj = raw as Record<string, unknown>;

  const strokeColor =
    typeof obj.currentItemStrokeColor === "string" &&
    obj.currentItemStrokeColor.trim().length > 0
      ? obj.currentItemStrokeColor.trim()
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeColor;

  const backgroundColor =
    typeof obj.currentItemBackgroundColor === "string" &&
    obj.currentItemBackgroundColor.trim().length > 0
      ? obj.currentItemBackgroundColor.trim()
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemBackgroundColor;

  const fillStyle =
    typeof obj.currentItemFillStyle === "string" &&
    VALID_FILL_STYLES.has(obj.currentItemFillStyle as ExcalidrawFillStyle)
      ? (obj.currentItemFillStyle as ExcalidrawFillStyle)
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemFillStyle;

  const strokeWidth =
    typeof obj.currentItemStrokeWidth === "number" &&
    Number.isFinite(obj.currentItemStrokeWidth) &&
    obj.currentItemStrokeWidth > 0
      ? obj.currentItemStrokeWidth
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeWidth;

  const strokeStyle =
    typeof obj.currentItemStrokeStyle === "string" &&
    VALID_STROKE_STYLES.has(obj.currentItemStrokeStyle as ExcalidrawStrokeStyle)
      ? (obj.currentItemStrokeStyle as ExcalidrawStrokeStyle)
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeStyle;

  const roughness =
    typeof obj.currentItemRoughness === "number" &&
    Number.isFinite(obj.currentItemRoughness) &&
    obj.currentItemRoughness >= 0 &&
    obj.currentItemRoughness <= 2
      ? obj.currentItemRoughness
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemRoughness;

  const roundness =
    typeof obj.currentItemRoundness === "string" &&
    VALID_ROUNDNESS_TYPES.has(obj.currentItemRoundness as ExcalidrawStrokeRoundness)
      ? (obj.currentItemRoundness as ExcalidrawStrokeRoundness)
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemRoundness;

  const opacity =
    typeof obj.currentItemOpacity === "number" &&
    Number.isFinite(obj.currentItemOpacity)
      ? Math.min(100, Math.max(0, Math.round(obj.currentItemOpacity)))
      : DEFAULT_DRAWING_TOOL_PREFS.currentItemOpacity;

  if (
    strokeColor === DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeColor &&
    backgroundColor === DEFAULT_DRAWING_TOOL_PREFS.currentItemBackgroundColor &&
    fillStyle === DEFAULT_DRAWING_TOOL_PREFS.currentItemFillStyle &&
    strokeWidth === DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeWidth &&
    strokeStyle === DEFAULT_DRAWING_TOOL_PREFS.currentItemStrokeStyle &&
    roughness === DEFAULT_DRAWING_TOOL_PREFS.currentItemRoughness &&
    roundness === DEFAULT_DRAWING_TOOL_PREFS.currentItemRoundness &&
    opacity === DEFAULT_DRAWING_TOOL_PREFS.currentItemOpacity
  ) {
    return DEFAULT_DRAWING_TOOL_PREFS;
  }

  return Object.freeze({
    currentItemStrokeColor: strokeColor,
    currentItemBackgroundColor: backgroundColor,
    currentItemFillStyle: fillStyle,
    currentItemStrokeWidth: strokeWidth,
    currentItemStrokeStyle: strokeStyle,
    currentItemRoughness: roughness,
    currentItemRoundness: roundness,
    currentItemOpacity: opacity,
  });
}

export const MIN_STROKE_SMOOTHING = 0;
export const MAX_STROKE_SMOOTHING = 1;
export const DEFAULT_STROKE_SMOOTHING = 0.5;
export const LOW_LATENCY_STROKE_SMOOTHING = 0;

export function clampStrokeSmoothing(value: number): number {
  if (!Number.isFinite(value)) {
    return DEFAULT_STROKE_SMOOTHING;
  }
  return Math.min(
    MAX_STROKE_SMOOTHING,
    Math.max(MIN_STROKE_SMOOTHING, Number(value.toFixed(4)))
  );
}

export function detectDefaultStrokeSmoothing(): number {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return DEFAULT_STROKE_SMOOTHING;
  }
  try {
    const ua = navigator.userAgent || "";
    const isAndroidLargeScreen =
      /\bAndroid\b/i.test(ua) && !/\bMobile\b/i.test(ua);
    const isSmartBoardOrTvUa =
      /\b(TV|SmartTV|Large Screen|Maxhub|ViewSonic|Promethean|Newline)\b/i.test(ua);
    const maxScreenDim = Math.max(
      window.screen?.width ?? 0,
      window.screen?.height ?? 0
    );
    const isLargeTouchPanel =
      navigator.maxTouchPoints > 0 && maxScreenDim >= 1920;

    if (isAndroidLargeScreen || isSmartBoardOrTvUa || isLargeTouchPanel) {
      return LOW_LATENCY_STROKE_SMOOTHING;
    }
  } catch {
    return DEFAULT_STROKE_SMOOTHING;
  }
  return DEFAULT_STROKE_SMOOTHING;
}

export function getSystemTheme(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  try {
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? true;
    return prefersDark ? "dark" : "light";
  } catch {
    return "dark";
  }
}

export function resolveInitialClientLocale(): LocaleCode {
  return "vi";
}

/**
 * Hydration-safe browser store for canvas theme mode preference.
 */
export const themeStore = createBrowserStore<ThemeMode>({
  key: THEME_STORAGE_KEY,
  serverFallback: "dark",
  clientDefault: () => getSystemTheme(),
  parse: (raw: string): ThemeMode => {
    const clean = raw.replace(/^"|"$/g, "");
    if (clean === "light" || clean === "dark") {
      return clean;
    }
    return getSystemTheme();
  },
  serialize: (val: ThemeMode): string => val,
});

/**
 * Hydration-safe browser store for UI and Excalidraw locale preference.
 */
export const localeStore = createBrowserStore<LocaleCode>({
  key: LOCALE_STORAGE_KEY,
  serverFallback: "vi",
  clientDefault: () => resolveInitialClientLocale(),
  parse: (raw: string): LocaleCode => {
    const clean = raw.replace(/^"|"$/g, "");
    if (isLocaleCode(clean)) {
      return clean;
    }
    return resolveInitialClientLocale();
  },
  serialize: (val: LocaleCode): string => val,
});

/**
 * Hydration-safe browser store for active educational STEM subject disciplines.
 */
export const subjectDisciplinesStore = createBrowserStore<readonly SubjectCategoryId[]>({
  key: CATEGORY_STORAGE_KEY,
  serverFallback: STEM_SUBJECT_IDS,
  clientDefault: () => STEM_SUBJECT_IDS,
  parse: (raw: string): readonly SubjectCategoryId[] => {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return Object.freeze(parsed as SubjectCategoryId[]);
      }
      return STEM_SUBJECT_IDS;
    } catch {
      return STEM_SUBJECT_IDS;
    }
  },
  serialize: (val: readonly SubjectCategoryId[]): string => JSON.stringify(val),
});

/**
 * Hydration-safe browser store for freedraw stroke smoothing level in [0, 1].
 */
export const strokeSmoothingStore = createBrowserStore<number>({
  key: STROKE_SMOOTHING_STORAGE_KEY,
  serverFallback: DEFAULT_STROKE_SMOOTHING,
  clientDefault: () => detectDefaultStrokeSmoothing(),
  parse: (raw: string): number => {
    const clean = raw.replace(/^"|"$/g, "").trim();
    if (clean === "") {
      return detectDefaultStrokeSmoothing();
    }
    const parsed = Number(clean);
    if (Number.isFinite(parsed)) {
      return clampStrokeSmoothing(parsed);
    }
    return detectDefaultStrokeSmoothing();
  },
  serialize: (val: number): string => String(clampStrokeSmoothing(val)),
});

/**
 * Hydration-safe browser store for user drawing tool style preferences across slides and reloads.
 */
export const drawingToolPrefsStore = createBrowserStore<DrawingToolPreferences>({
  key: DRAWING_TOOL_PREFS_STORAGE_KEY,
  serverFallback: DEFAULT_DRAWING_TOOL_PREFS,
  clientDefault: () => DEFAULT_DRAWING_TOOL_PREFS,
  parse: (raw: string): DrawingToolPreferences => {
    try {
      return sanitizeDrawingToolPrefs(JSON.parse(raw));
    } catch {
      return DEFAULT_DRAWING_TOOL_PREFS;
    }
  },
  serialize: (val: DrawingToolPreferences): string =>
    JSON.stringify(sanitizeDrawingToolPrefs(val)),
});

/**
 * Synchronizes any modified drawing tool style attributes from Excalidraw's live appState
 * into `drawingToolPrefsStore` in O(1) without redundant localStorage writes.
 */
export function syncDrawingToolPrefsFromAppState(
  appState: Partial<DrawingToolPreferences> | null | undefined
): DrawingToolPreferences {
  const current = drawingToolPrefsStore.getSnapshot();
  if (!appState || typeof appState !== "object") {
    return current;
  }
  if (
    (appState.currentItemStrokeColor === undefined ||
      appState.currentItemStrokeColor === current.currentItemStrokeColor) &&
    (appState.currentItemBackgroundColor === undefined ||
      appState.currentItemBackgroundColor === current.currentItemBackgroundColor) &&
    (appState.currentItemFillStyle === undefined ||
      appState.currentItemFillStyle === current.currentItemFillStyle) &&
    (appState.currentItemStrokeWidth === undefined ||
      appState.currentItemStrokeWidth === current.currentItemStrokeWidth) &&
    (appState.currentItemStrokeStyle === undefined ||
      appState.currentItemStrokeStyle === current.currentItemStrokeStyle) &&
    (appState.currentItemRoughness === undefined ||
      appState.currentItemRoughness === current.currentItemRoughness) &&
    (appState.currentItemRoundness === undefined ||
      appState.currentItemRoundness === current.currentItemRoundness) &&
    (appState.currentItemOpacity === undefined ||
      appState.currentItemOpacity === current.currentItemOpacity)
  ) {
    return current;
  }
  const candidate = sanitizeDrawingToolPrefs({
    currentItemStrokeColor:
      appState.currentItemStrokeColor ?? current.currentItemStrokeColor,
    currentItemBackgroundColor:
      appState.currentItemBackgroundColor ?? current.currentItemBackgroundColor,
    currentItemFillStyle:
      appState.currentItemFillStyle ?? current.currentItemFillStyle,
    currentItemStrokeWidth:
      appState.currentItemStrokeWidth ?? current.currentItemStrokeWidth,
    currentItemStrokeStyle:
      appState.currentItemStrokeStyle ?? current.currentItemStrokeStyle,
    currentItemRoughness:
      appState.currentItemRoughness ?? current.currentItemRoughness,
    currentItemRoundness:
      appState.currentItemRoundness ?? current.currentItemRoundness,
    currentItemOpacity:
      appState.currentItemOpacity ?? current.currentItemOpacity,
  });

  if (
    candidate.currentItemStrokeColor === current.currentItemStrokeColor &&
    candidate.currentItemBackgroundColor === current.currentItemBackgroundColor &&
    candidate.currentItemFillStyle === current.currentItemFillStyle &&
    candidate.currentItemStrokeWidth === current.currentItemStrokeWidth &&
    candidate.currentItemStrokeStyle === current.currentItemStrokeStyle &&
    candidate.currentItemRoughness === current.currentItemRoughness &&
    candidate.currentItemRoundness === current.currentItemRoundness &&
    candidate.currentItemOpacity === current.currentItemOpacity
  ) {
    return current;
  }

  drawingToolPrefsStore.set(candidate);
  return candidate;
}
